import React from 'react';
import cn from 'classnames';
import {
    Field,
    FormikErrors,
    FormikTouched,
    FormikValues,
    useField,
} from 'formik';
import slugify from 'slugify';
import {
    DataField,
    DataList,
    DocumentInfo,
    FormField,
    ImageInfo,
    Site,
    VideoInfo,
} from './dataType';
import EditorField from '../editor/EditorField';
import { LabelField } from './LabelField';
import ToastMessage from '../ui/ToastMessage';
import { ImageUpload } from '../ui/ImageUpload';
import { ImagesInput } from '../ui/ImagesInput';
import { VideosInput } from '../ui/VideosInput';
import { getFormikType } from '../../app/[locale]/admin/lib/util';
import { DocumentsInput } from '../ui/DocumentsInput';
import { ToggleButton } from '../ui/ToggleButton';
import { FormSelect } from '@/components/FormSelect';
import ListInput from './ListInput';
import RadioButton from '../ui/RadioButton';
import { PhoneNumberInput } from '../ui/PhoneNumberInput';
import { CustomDatePicker } from '../ui/CustomDatePicker';

type FormikFieldProps = {
    id?: number | string;
    dataField: DataField | FormField;
    editable?: boolean;
    fieldList?: DataList;
    locale: string;
    pathname?: string;
    site?: Site;
    dirty: boolean;
    errors: FormikErrors<FormikValues>;
    touched: FormikTouched<FormikValues>;
    values?: FormikValues;
    setValues?: (
        values: FormikValues,
        shouldValidate?: boolean | undefined,
    ) => Promise<unknown>;
    onChanges: (value: unknown) => void;
    alignHorizontal?: boolean;
};

export const FormikField = ({
    id,
    dataField,
    fieldList,
    locale,
    pathname,
    site,
    dirty,
    editable = false,
    errors,
    touched,
    values,
    setValues,
    onChanges,
    alignHorizontal,
}: FormikFieldProps) => {
    const [field, , helpers] = useField(dataField.name);
    const notify = React.useCallback((type: string, message: string) => {
        ToastMessage({ type, message });
    }, []);
    const handleBlur = (value: string) => {
        onChanges(value);
        const slugifyTypes = ['slug', 'key-link'];
        if (slugifyTypes.includes(dataField.dataType)) {
            if (value !== '' && !value.includes('://')) {
                const oldValue = value;
                const newValue = slugify(oldValue);
                if (oldValue !== newValue) {
                    helpers.setValue(newValue);
                    if (dataField.dataType === 'slug') {
                        notify(
                            'info',
                            `<div><p>${oldValue}</p><p>a été transformé en:</p><p>${newValue}</p><p>pour respecter la conformité des URL.</p></div>`,
                        );
                    }
                }
            }
        }
    };

    const containerClasses = alignHorizontal
        ? 'block md:flex md:flex-row md:items-center md:space-x-4 w-full'
        : 'block md:flex md:flex-col md:space-y-2 w-full';
    const labelClasses = alignHorizontal ? 'xl:w-1/2 md:w-96' : '';

    switch (dataField.dataType) {
        case 'boolean':
            return (
                <div className={containerClasses}>
                    <LabelField
                        className={labelClasses}
                        dirty={dirty}
                        errors={errors}
                        field={dataField}
                        locale={locale}
                        touched={touched}
                    />
                    <Field
                        component={ToggleButton}
                        onChange={(value: boolean) => {
                            onChanges(value);
                            helpers.setValue(value);
                        }}
                        type={dataField.dataType}
                        value={field.value}
                    />
                </div>
            );
        case 'select-number':
        case 'select-text':
            {
                if (fieldList) {
                    const typeAbbr = dataField.dataType.replace('select-', '');

                    const parentValue: number | string | undefined =
                        values?.[fieldList?.parentField || -1];

                    const options = fieldList?.parentField
                        ? fieldList?.options?.filter(
                              f =>
                                  f.parentValue == parentValue ||
                                  f.parentValue == 0,
                          )
                        : fieldList?.options;

                    if (!options?.find(x => x.value === field?.value)) {
                        if (
                            values &&
                            values[dataField.name] !== dataField.defaultValue
                        ) {
                            values[dataField.name] = dataField.defaultValue;
                            if (setValues) {
                                setValues({ ...values });
                            }
                        }
                    }

                    if (options && options.length) {
                        return (
                            <div className={containerClasses}>
                                <LabelField
                                    className={labelClasses}
                                    dirty={dirty}
                                    errors={errors}
                                    field={dataField}
                                    locale={locale}
                                    touched={touched}
                                />
                                <Field
                                    component={FormSelect}
                                    field={fieldList.field}
                                    locale={locale}
                                    name={dataField.name}
                                    onChange={(value: number | string) => {
                                        onChanges(value);
                                        helpers.setValue(value);
                                    }}
                                    options={options}
                                    type={typeAbbr}
                                    value={values?.[dataField.name] || ''}
                                />
                            </div>
                        );
                    }
                }
            }
            break;
        case 'radio-list':
            {
                if (fieldList) {
                    if (fieldList.options && fieldList.options?.length) {
                        return (
                            <div className={containerClasses}>
                                <LabelField
                                    className={labelClasses}
                                    dirty={dirty}
                                    errors={errors}
                                    field={dataField}
                                    locale={locale}
                                    touched={touched}
                                />
                                <Field
                                    component={RadioButton}
                                    field={fieldList.field}
                                    onChange={(value: string) => {
                                        onChanges(value);
                                        helpers.setValue(value);
                                    }}
                                    options={fieldList.options}
                                    orientation="horizontal"
                                    tag={dataField.name}
                                    value={values?.[dataField.name] || ''}
                                />
                            </div>
                        );
                    }
                }
            }
            break;
        case 'image':
            return (
                <>
                    <LabelField
                        dirty={dirty}
                        errors={errors}
                        field={dataField}
                        locale={locale}
                        touched={touched}
                    />
                    <Field
                        bucket={site?.bucket}
                        component={ImageUpload}
                        folder={pathname}
                        imageHeight={208}
                        itemId={id}
                        locale={locale}
                        onChange={(value: File) => {
                            onChanges(value);
                            helpers.setValue(value);
                        }}
                        type={dataField.dataType}
                        value={field.value}
                    />
                </>
            );
        case 'images':
            return (
                <>
                    <LabelField
                        dirty={dirty}
                        errors={errors}
                        field={dataField}
                        locale={locale}
                        touched={touched}
                    />
                    <Field
                        bucket={site?.bucket}
                        component={ImagesInput}
                        folder={pathname}
                        itemId={id}
                        locale={locale}
                        onChange={(value: ImageInfo[]) => {
                            onChanges(value);
                            helpers.setValue(value);
                        }}
                        type={dataField.dataType}
                        value={field.value}
                    />
                </>
            );
        case 'videos':
            return (
                <>
                    <LabelField
                        dirty={dirty}
                        errors={errors}
                        field={dataField}
                        locale={locale}
                        touched={touched}
                    />
                    <Field
                        component={VideosInput}
                        locale={locale}
                        onChange={(value: VideoInfo[]) => {
                            onChanges(value);
                            helpers.setValue(value);
                        }}
                        type={dataField.dataType}
                        value={field.value}
                    />
                </>
            );
        case 'documents':
            return (
                <>
                    <LabelField
                        dirty={dirty}
                        errors={errors}
                        field={dataField}
                        locale={locale}
                        touched={touched}
                    />
                    <Field
                        bucket={site?.bucket}
                        component={DocumentsInput}
                        folder={pathname}
                        itemId={id}
                        locale={locale}
                        onChange={(value: DocumentInfo[]) => {
                            onChanges(value);
                            helpers.setValue(value);
                        }}
                        type={dataField.dataType}
                        value={field.value}
                    />
                </>
            );
            break;
        case 'editor':
            return (
                <>
                    <Field
                        component={EditorField}
                        dataField={dataField}
                        errors={errors}
                        id={dataField.name}
                        locale={locale}
                        name={dataField.name}
                        onChange={(value: unknown) => {
                            onChanges(value);
                        }}
                        touched={touched}
                    />
                </>
            );
        case 'date':
            return (
                <div className={containerClasses}>
                    <LabelField
                        className={labelClasses}
                        dirty={dirty}
                        errors={errors}
                        field={dataField}
                        locale={locale}
                        touched={touched}
                    />
                    <Field
                        component={CustomDatePicker}
                        dataField={dataField}
                        errors={errors}
                        id={dataField.name}
                        locale={locale}
                        name={dataField.name}
                        onChange={(value: unknown) => {
                            onChanges(value);
                            helpers.setValue(value);
                        }}
                        touched={touched}
                    />
                </div>
            );
        case 'list':
            return (
                <>
                    <LabelField
                        dirty={dirty}
                        errors={errors}
                        field={dataField}
                        locale={locale}
                        touched={touched}
                    />
                    <ListInput
                        editable={editable}
                        name={dataField.name}
                        onChange={value => {
                            onChanges(value);
                            helpers.setValue(value);
                        }}
                        options={fieldList?.options || []}
                        placeholder="Ajouter un élément"
                    />
                </>
            );
        case 'phone':
            return (
                <div className={containerClasses}>
                    <LabelField
                        className={labelClasses}
                        dirty={dirty}
                        errors={errors}
                        field={dataField}
                        locale={locale}
                        touched={touched}
                    />
                    <Field
                        component={PhoneNumberInput}
                        dataField={dataField}
                        errors={errors}
                        locale={locale}
                        name={dataField.name}
                        onChange={(value: unknown) => {
                            onChanges(value);
                            helpers.setValue(value);
                        }}
                        touched={touched}
                    />
                </div>
            );
        case 'label':
            return (
                <div className={containerClasses}>
                    <LabelField
                        className={labelClasses}
                        dirty={dirty}
                        errors={errors}
                        field={dataField}
                        locale={locale}
                        touched={touched}
                    />
                    {field.value ?? ''}
                </div>
            );

        default:
            return (
                <div className={containerClasses}>
                    <LabelField
                        className={labelClasses}
                        dirty={dirty}
                        errors={errors}
                        field={dataField}
                        locale={locale}
                        touched={touched}
                    />
                    <Field
                        className={cn(
                            'w-full flex grow bg-white border border-gray-300 text-gray-900 rounded-lg focus:ring-primary focus:border-primary p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary dark:focus:ring-primary',
                            errors &&
                                touched &&
                                errors[dataField.name] &&
                                touched[dataField.name] &&
                                'bg-red-50',
                        )}
                        id={dataField.name}
                        name={dataField.name}
                        onBlur={(e: React.ChangeEvent<HTMLInputElement>) => {
                            handleBlur(e.target.value);
                        }}
                        type={getFormikType(dataField.dataType)}
                        value={field.value ?? ''}
                    />
                </div>
            );
    }
};
